"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import gsap from "gsap";
import { GET_CMS_REVIEWS } from "@/graphql/queries";

const B = {
  darkest: "#64483c",
  mid:     "#a8704e",
  warm:    "#bf875d",
  light:   "#d8aa84",
  muted:   "#c5a08a",
  pale:    "#dbc5ae",
  cream:   "#fad5b2",
};



// ── Fallback testimonials (for when no reviews are loaded yet) ────────
const fallbackTestimonials = [
  {
    name: "Loading...",
    date: "—",
    rating: 5,
    text: "Fetching customer reviews...",
  },
];

function Stars({ count }: { count: number }) {
  return (
    <div style={{ display: "flex", gap: 2, marginBottom: 8 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} style={{ color: i < count ? B.light : `${B.darkest}80`, fontSize: 13 }}>★</span>
      ))}
    </div>
  );
}

function Card({ t, compact = false }: { t: any; compact?: boolean }) {
  return (
    <div style={{
      width: "100%",
      background: `linear-gradient(150deg, ${B.darkest} 0%, #4a3328 100%)`,
      border: `1px solid ${B.warm}28`,
      borderRadius: compact ? 12 : 16,
      padding: compact ? "14px 12px" : "20px 18px",
      marginBottom: compact ? 12 : 16,
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      boxSizing: "border-box" as const,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: compact ? 34 : 40,
          height: compact ? 34 : 40,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${B.light}, ${B.mid})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: compact ? 13 : 16,
          fontWeight: 700,
          color: B.darkest,
          flexShrink: 0,
        }}>
          {t.name[0]}
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{
            color: B.light, fontSize: compact ? 10 : 11, fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase" as const,
            margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const,
          }}>
            {t.name}
          </p>
          <p style={{ color: B.muted, fontSize: 9, margin: "2px 0 0", opacity: 0.75 }}>
            {t.is_verified ? "Verified Buyer" : "Customer"} {t.date && `· ${t.date}`}
          </p>
        </div>
      </div>

      <Stars count={t.rating} />

      <div style={{ height: 1, background: `linear-gradient(to right, ${B.mid}44, transparent)`, marginBottom: 10 }} />

      <p style={{
        color: B.pale,
        fontSize: compact ? 11 : 13,
        lineHeight: 1.65,
        margin: 0,
        fontStyle: "italic",
        fontFamily: "Georgia, serif",
      }}>
        "{t.text}"
      </p>
    </div>
  );
}

function Lane({
  items,
  direction,
  speed,
  compact,
}: {
  items: any[];
  direction: "up" | "down";
  speed: number;
  compact: boolean;
}) {
  const wrapRef  = useRef<HTMLDivElement>(null);
  const topRef   = useRef<HTMLDivElement>(null);
  const btmRef   = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const laneRef  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const top = topRef.current;
    const wrap = wrapRef.current;
    if (!top || !wrap) return;

    // Small delay to ensure DOM is fully laid out before measuring
    const timer = setTimeout(() => {
      const setH = top.offsetHeight;
      if (setH === 0) return; // Height not ready, bail
      
      const fromY = direction === "down" ? -setH : 0;
      const toY   = direction === "down" ? 0 : -setH;

      gsap.set(wrap, { y: fromY });

      tweenRef.current = gsap.to(wrap, {
        y:        toY,
        duration: speed,
        ease:     "none",
        repeat:   -1,
      });
    }, 100);

    const el = laneRef.current;
    const pause  = () => tweenRef.current?.pause();
    const resume = () => tweenRef.current?.play();
    el?.addEventListener("mouseenter", pause);
    el?.addEventListener("mouseleave", resume);

    return () => {
      clearTimeout(timer);
      tweenRef.current?.kill();
      el?.removeEventListener("mouseenter", pause);
      el?.removeEventListener("mouseleave", resume);
    };
  }, [direction, speed]);

  return (
    <div
      ref={laneRef}
      style={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        maskImage: "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
      }}
    >
      <div ref={wrapRef} style={{ willChange: "transform" }}>
        <div ref={topRef}>
          {items.map((t, i) => <Card key={`a-${i}`} t={t} compact={compact} />)}
        </div>
        <div ref={btmRef}>
          {items.map((t, i) => <Card key={`b-${i}`} t={t} compact={compact} />)}
        </div>
      </div>
    </div>
  );
}

export default function TestimonialTicker() {
  const [isMobile, setIsMobile] = useState(false);
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);

  // ── Fetch reviews from GraphQL ───────────────────────────────────────
  const { data, loading } = useQuery(GET_CMS_REVIEWS, {
    variables: { where: { type: { _eq: "REVIEW" } } },
    fetchPolicy: "cache-and-network",
  });

  // ── Transform GraphQL data to component format ────────────────────────
  useEffect(() => {
    if (data?.tenant_org_web_configuration?.[0]) {
      const config = data.tenant_org_web_configuration[0];
      const metas = config.web_configuration_meta || [];

      if (metas.length > 0) {
        const transformed = metas.map((meta: any) => {
          const dv = meta.data_values || {};
          return {
            name: dv.reviewer_name || "Anonymous",
            date: dv.date || "",
            rating: dv.rating || 5,
            text: dv.review_text || "",
            is_verified: dv.is_verified !== false,
          };
        });

        setTestimonials(transformed);
      }
    }
  }, [data]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Distribute reviews evenly across 3 desktop columns
  const col1 = testimonials.filter((_, i) => i % 3 === 0);
  const col2 = testimonials.filter((_, i) => i % 3 === 1);
  const col3 = testimonials.filter((_, i) => i % 3 === 2);

  // 2 mobile columns
  const mobileL = testimonials.filter((_, i) => i % 2 === 0);
  const mobileR = testimonials.filter((_, i) => i % 2 === 1);

  return (
    <section style={{
      width: "100%",
      padding: isMobile ? "0px" : "80px 0 68px",
      overflow: "hidden",
      fontFamily: "'Nunito', sans-serif",
    }}>
      {/* Heading */}
      <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 50, padding: "0 20px" }}>
        <p style={{
          color: B.muted, fontSize: 10, letterSpacing: "0.28em",
          textTransform: "uppercase", margin: "0 0 8px",
        }}>
          What our customers say
        </p>
        <h2 style={{
          color: B.cream,
          fontSize: isMobile ? "24px" : "clamp(28px, 3.5vw, 44px)",
          margin: "0 0 16px", fontWeight: 300, letterSpacing: "0.05em",
          fontFamily: "Georgia, 'Times New Roman', serif",
        }}>
          Loved by Thousands
        </h2>
        <div style={{
          margin: "0 auto", width: 48, height: 1,
          background: `linear-gradient(to right, transparent, ${B.warm}, transparent)`,
        }} />
      </div>

      {/* Ticker */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr",
        gap: isMobile ? 10 : 20,
        width: isMobile ? "calc(100% - 28px)" : "90%",
        maxWidth: 1100,
        height: isMobile ? 420 : 580,
        margin: "0 auto",
      }}>
        {isMobile ? (
          <>
            <Lane items={mobileL} direction="up"   speed={32} compact />
            <Lane items={mobileR} direction="down" speed={40} compact />
          </>
        ) : (
          <>
            <Lane items={col1} direction="up"   speed={30} compact={false} />
            <Lane items={col2} direction="down" speed={38} compact={false} />
            <Lane items={col3} direction="up"   speed={34} compact={false} />
          </>
        )}
      </div>

      <p style={{
        textAlign: "center", color: `${B.muted}45`, fontSize: 9,
        letterSpacing: "0.2em", textTransform: "uppercase", marginTop: 24,
      }}>
        {loading ? "Loading reviews..." : isMobile ? "Tap a card to read" : "Hover to pause"}
      </p>
    </section>
  );
}